import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Post, Comment } from '@contentflow/shared-types';
import { Link, useParams } from 'react-router-dom';
import { marked } from 'marked';
import UtilityCard from './UtilityCard';
import './PostPage.css';

const toTitleCase = (str: string) => {
  return str.replace(
    /\w\S*/g,
    function (txt) {
      return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
    }
  );
};

interface PostPageProps {
  apiBaseUrl?: string;
}

const PostPage: React.FC<PostPageProps> = ({ apiBaseUrl }) => {
  const { slug } = useParams<{ slug: string }>();
  const [post, setPost] = useState<Post | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [settings, setSettings] = useState({ headScripts: '', footerScripts: '', adPlaceholder: '', blogHeaderBanner: '' });
  const [siteConfig, setSiteConfig] = useState({
    site_title: 'SolveSmartHome',
    site_tagline: 'Solutions from the smart home community.',
    site_description: 'We distill r/homeautomation and r/smarthome into actionable fixes. Find the right gear to solve lag, offline devices, and ecosystem lock-in.',
    site_categories: ['Smart Hubs', 'Zigbee', 'Matter', 'WiFi Devices', 'Voice Assistants', 'Lighting', 'Security', 'Local Control'],
    site_footer_text: 'Stay updated with the latest smart home fixes.'
  });

  // Comments state
  const [comments, setComments] = useState<Comment[]>([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [newComment, setNewComment] = useState({
    author_name: '',
    author_email: '',
    content: ''
  });
  const [submittingComment, setSubmittingComment] = useState(false);
  const [shareMessage, setShareMessage] = useState('');
  const [showStickyJump, setShowStickyJump] = useState(false);
  const utilityCardRef = useRef<HTMLDivElement>(null);

  const fetchSettings = useCallback(async () => {
    try {
      console.log('Fetching settings from:', `${apiBaseUrl}/settings`);
      const response = await fetch(`${apiBaseUrl}/settings`);
      const data = await response.json();
      console.log('Settings response:', data);
      if (response.ok) {
        const newSettings = {
          headScripts: data.data.head_scripts || '',
          footerScripts: data.data.footer_scripts || '',
          adPlaceholder: data.data.ad_placeholder || '',
          blogHeaderBanner: data.data.blog_header_banner_base64 || ''
        };
        console.log('Setting adPlaceholder:', newSettings.adPlaceholder);
        setSettings(newSettings);
      }
    } catch (err) {
      console.error('Failed to fetch settings:', err);
    }
  }, [apiBaseUrl]);

  const fetchSiteConfig = useCallback(async () => {
    try {
      const response = await fetch(`${apiBaseUrl}/config`);
      const data = await response.json();
      if (response.ok && data.success) {
        setSiteConfig(data.data);
      }
    } catch (err) {
      console.error('Failed to fetch site config:', err);
    }
  }, [apiBaseUrl]);

  const fetchComments = useCallback(async (postId: string) => {
    if (!postId) return;

    setCommentsLoading(true);
    try {
      const response = await fetch(`${apiBaseUrl}/comments?post_id=${postId}`);
      const data = await response.json();
      if (response.ok && data.success) {
        setComments(data.data);
      }
    } catch (err) {
      console.error('Failed to fetch comments:', err);
    } finally {
      setCommentsLoading(false);
    }
  }, [apiBaseUrl]);

  const handleVote = async (commentId: string, voteType: 'upvote' | 'downvote') => {
    try {
      const response = await fetch(`${apiBaseUrl}/comments/vote`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          comment_id: commentId,
          vote_type: voteType
        })
      });

      if (response.ok) {
        // Refresh comments to get updated vote counts
        if (post?.id) {
          fetchComments(post.id);
        }
      }
    } catch (err) {
      console.error('Failed to vote on comment:', err);
    }
  };

  const handleSubmitComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!post?.id || !newComment.author_name || !newComment.author_email || !newComment.content) {
      return;
    }

    setSubmittingComment(true);
    try {
      const response = await fetch(`${apiBaseUrl}/comments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          post_id: post.id,
          author_name: newComment.author_name,
          author_email: newComment.author_email,
          content: newComment.content
        })
      });

      if (response.ok) {
        // Reset form
        setNewComment({
          author_name: '',
          author_email: '',
          content: ''
        });
        // Refresh comments
        fetchComments(post.id);
      }
    } catch (err) {
      console.error('Failed to submit comment:', err);
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleShare = async () => {
    if (!post?.slug) return;

    const postUrl = `${window.location.origin}/post/${post.slug}`;

    try {
      // Try to use the modern Clipboard API first
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(postUrl);
        setShareMessage('Link copied to clipboard!');
      } else {
        // Fallback for older browsers or non-secure contexts
        const textArea = document.createElement('textarea');
        textArea.value = postUrl;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();

        try {
          document.execCommand('copy');
          setShareMessage('Link copied to clipboard!');
        } catch (err) {
          console.error('Failed to copy text: ', err);
          setShareMessage('Failed to copy link');
        }

        document.body.removeChild(textArea);
      }
    } catch (err) {
      console.error('Failed to copy: ', err);
      setShareMessage('Failed to copy link');
    }

    // Clear the message after 3 seconds
    setTimeout(() => {
      setShareMessage('');
    }, 3000);
  };

  useEffect(() => {
    if (slug) {
      fetchPost(slug);
    }
    fetchSettings();
    fetchSiteConfig();
  }, [slug, fetchSettings, fetchSiteConfig]);

  useEffect(() => {
    if (post?.id && showComments) {
      fetchComments(post.id);
    }
  }, [post?.id, showComments, fetchComments]);

  // Sticky "Jump to Solution" - show when user scrolls past the utility card
  const hasUtilityCard = !!(post?.uc_problem || post?.uc_solution || post?.uc_product_name);
  useEffect(() => {
    if (!hasUtilityCard || !utilityCardRef.current) return;

    const handleScroll = () => {
      const card = utilityCardRef.current;
      if (!card) return;
      const rect = card.getBoundingClientRect();
      setShowStickyJump(rect.bottom < 0);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // Check initial position
    return () => window.removeEventListener('scroll', handleScroll);
  }, [hasUtilityCard, post?.id]);

  // Cleanup: Always remove any divs and identity.js from head tag (invalid HTML / not needed for Step 1)
  useEffect(() => {
    const cleanup = () => {
      // Remove divs from head (invalid HTML)
      const allHeadDivs = document.head.querySelectorAll('div');
      allHeadDivs.forEach(div => div.remove());

      // Remove identity.js scripts (not needed for Step 1 integration)
      const identityScripts = document.head.querySelectorAll('script[src*="identity.js"]');
      identityScripts.forEach(script => script.remove());
    };

    // Run immediately and set up interval to check periodically (every 500ms)
    cleanup();
    const interval = setInterval(cleanup, 500);

    return () => clearInterval(interval);
  }, []); // Run on mount and cleanup on unmount

  // Inject head scripts
  useEffect(() => {
    // Always clean up any divs in head (invalid HTML) - run this on every render
    const allHeadDivs = document.head.querySelectorAll('div');
    allHeadDivs.forEach(div => div.remove());

    if (settings.headScripts) {
      // Remove any existing scripts and containers first
      const existingScripts = document.querySelectorAll('#contentflow-head-scripts');
      existingScripts.forEach(script => script.remove());

      // Remove any div containers that might have been left in head (invalid HTML)
      const existingDivs = document.head.querySelectorAll('div#contentflow-head-scripts, div[id="contentflow-head-scripts"]');
      existingDivs.forEach(div => div.remove());

      // Remove any existing Ezoic scripts to prevent conflicts
      const existingEzoicScripts = document.querySelectorAll('script[src*="ezoic"], script[src*="gatekeeperconsent"], script[src*="ezojs"]');
      existingEzoicScripts.forEach(script => script.remove());

      // Find the insertion point: after title tag, before CSS/JS
      const head = document.head;
      const titleTag = head.querySelector('title');
      const insertBefore = head.querySelector('link[rel="stylesheet"]') ||
        head.querySelector('script[src*="main."]') ||
        head.querySelector('script[src*="static/"]') ||
        null;

      // Get insertion reference: after title, or after last meta/link if no title
      let insertAfter: Node | null = null;
      if (titleTag) {
        insertAfter = titleTag;
      } else {
        // Fallback: find last meta or link tag
        const allMetaLinks = Array.from(head.querySelectorAll('meta, link'));
        if (allMetaLinks.length > 0) {
          insertAfter = allMetaLinks[allMetaLinks.length - 1];
        }
      }

      // Parse and execute scripts
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = settings.headScripts;

      // Extract and categorize scripts in the exact order needed
      const allScripts = Array.from(tempDiv.querySelectorAll('script'));

      // Filter out identity.js (not needed for Step 1 integration)
      const filteredScripts = allScripts.filter(script =>
        !(script.src && script.src.includes('identity.js'))
      );

      // 1. Privacy scripts (gatekeeperconsent/privacy/__gpp)
      const privacyScripts = filteredScripts.filter(script =>
        (script.src && (script.src.includes('gatekeeperconsent') || script.src.includes('privacy'))) ||
        (script.textContent && (script.textContent.includes('__gpp') || script.textContent.includes('privacy')))
      );

      // 2. sa.min.js (Ezoic header script)
      const saMinScripts = filteredScripts.filter(script =>
        script.src && script.src.includes('sa.min.js')
      );

      // 3. ezstandalone initialization scripts (inline initialization)
      const ezstandaloneScripts = filteredScripts.filter(script =>
        script.textContent && script.textContent.includes('ezstandalone') &&
        !privacyScripts.includes(script) && !saMinScripts.includes(script)
      );

      // 4. Other scripts (excluding identity.js which was already filtered)
      const otherScripts = filteredScripts.filter(script =>
        !privacyScripts.includes(script) &&
        !saMinScripts.includes(script) &&
        !ezstandaloneScripts.includes(script)
      );

      // Insert scripts in the exact order: privacy -> sa.min.js -> ezstandalone -> others
      const orderedScripts = [...privacyScripts, ...saMinScripts, ...ezstandaloneScripts, ...otherScripts];

      orderedScripts.forEach(script => {
        const newScript = document.createElement('script');

        if (script.src) {
          newScript.src = script.src;
          // Preserve async/defer attributes
          if (script.async) newScript.async = true;
          if (script.defer) newScript.defer = true;
          // sa.min.js should have async attribute
          if (script.src.includes('sa.min.js') && !script.async) {
            newScript.async = true;
          }
          // Add data-cfasync="false" for all external scripts
          newScript.setAttribute('data-cfasync', 'false');
        } else {
          newScript.textContent = script.textContent || '';
          // Add data-cfasync="false" for privacy and ezstandalone inline scripts
          if (script.textContent && (script.textContent.includes('__gpp') ||
            script.textContent.includes('privacy') ||
            script.textContent.includes('ezstandalone'))) {
            newScript.setAttribute('data-cfasync', 'false');
          }
        }

        // Insert at the correct position
        if (insertBefore) {
          head.insertBefore(newScript, insertBefore);
        } else if (insertAfter) {
          // Insert after the reference node
          if (insertAfter.nextSibling) {
            head.insertBefore(newScript, insertAfter.nextSibling);
          } else {
            head.appendChild(newScript);
          }
        } else {
          head.appendChild(newScript);
        }
      });

      // Note: Non-script content (like HTML comments, text nodes, etc.) is ignored
      // Only script elements are inserted into the head tag

      // After Ezoic scripts are injected, ensure app's main JS loads AFTER Ezoic scripts
      // Order should be: Ezoic scripts -> CSS -> App's main JS
      const allHeadScripts = Array.from(head.querySelectorAll('script'));
      const appMainScripts = allHeadScripts.filter(script =>
        script.src && (script.src.includes('/static/js/main.') || script.src.includes('/main.') || script.src.match(/\/main\.[a-f0-9]+\.js/))
      );

      // Find the last Ezoic script (to insert app JS after it)
      const allEzoicScripts = allHeadScripts.filter(script =>
        script.src && (script.src.includes('ezoic') || script.src.includes('gatekeeperconsent') || script.src.includes('ezojs')) ||
        script.textContent && (script.textContent.includes('ezstandalone') || script.textContent.includes('__gpp'))
      );

      if (allEzoicScripts.length > 0 && appMainScripts.length > 0) {
        const lastEzoicScript = allEzoicScripts[allEzoicScripts.length - 1];
        const cssLink = head.querySelector('link[rel="stylesheet"]');

        // Move app's main JS to after Ezoic scripts (and after CSS if CSS exists and is after Ezoic)
        appMainScripts.forEach(script => {
          const scriptIndex = Array.from(head.childNodes).indexOf(script);
          const lastEzoicIndex = Array.from(head.childNodes).indexOf(lastEzoicScript);

          // If script is before Ezoic scripts, move it after
          if (scriptIndex < lastEzoicIndex) {
            // Determine insertion point: after CSS if CSS exists and is after Ezoic, otherwise after Ezoic
            let insertAfter: Node = lastEzoicScript;
            if (cssLink) {
              const cssIndex = Array.from(head.childNodes).indexOf(cssLink);
              if (cssIndex > lastEzoicIndex) {
                // CSS is after Ezoic, so insert JS after CSS
                insertAfter = cssLink;
              }
            }

            if (insertAfter.nextSibling) {
              head.insertBefore(script, insertAfter.nextSibling);
            } else {
              head.appendChild(script);
            }
          }
        });
      }

      // Also remove any identity.js scripts that might have been added
      const identityScripts = head.querySelectorAll('script[src*="identity.js"]');
      identityScripts.forEach(script => script.remove());
    }
  }, [settings.headScripts]);

  // Inject footer scripts
  useEffect(() => {
    if (settings.footerScripts) {
      // Remove any existing scripts first
      const existingScripts = document.querySelectorAll('#contentflow-footer-scripts');
      existingScripts.forEach(script => script.remove());

      // Create a container div
      const container = document.createElement('div');
      container.id = 'contentflow-footer-scripts';
      container.style.display = 'none'; // Hide the container

      // Parse and execute scripts
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = settings.footerScripts;

      // Extract and execute each script
      const scripts = tempDiv.querySelectorAll('script');
      scripts.forEach(script => {
        const newScript = document.createElement('script');
        if (script.src) {
          newScript.src = script.src;
          newScript.async = script.async;
          newScript.defer = script.defer;
        } else {
          newScript.textContent = script.textContent;
        }
        document.body.appendChild(newScript);
      });

      // Add any non-script content to the container
      const nonScripts = Array.from(tempDiv.childNodes).filter(node =>
        node.nodeType !== Node.ELEMENT_NODE || (node as Element).tagName !== 'SCRIPT'
      );
      nonScripts.forEach(node => container.appendChild(node.cloneNode(true)));

      document.body.appendChild(container);
    }
  }, [settings.footerScripts]);

  const fetchPost = async (postSlug: string) => {
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${apiBaseUrl}/posts/${postSlug}`);
      const data = await response.json();

      if (response.ok) {
        setPost(data.data);
        // Update page title and meta description
        document.title = `${data.data.title} - ${siteConfig.site_title}`;
        const metaDescription = document.querySelector('meta[name="description"]');
        if (metaDescription) {
          metaDescription.setAttribute('content', data.data.meta_description || data.data.title);
        } else {
          const meta = document.createElement('meta');
          meta.name = 'description';
          meta.content = data.data.meta_description || data.data.title;
          document.head.appendChild(meta);
        }
      } else if (response.status === 404) {
        setError('Post not found');
      } else {
        setError(data.message || 'Failed to fetch post');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatReadingTime = (content: string) => {
    const wordsPerMinute = 200;
    const wordCount = content.replace(/<[^>]*>/g, '').split(/\s+/).length;
    const readingTime = Math.ceil(wordCount / wordsPerMinute);
    return `${readingTime} min`;
  };

  const renderMarkdown = (markdown: string): string => {
    try {
      // Use marked.parse for synchronous rendering
      return marked.parse(markdown) as string;
    } catch (error) {
      console.error('Error rendering markdown:', error);
      return markdown; // Fallback to plain text
    }
  };

  const renderMarkdownWithAds = (content: string, adPlaceholder: string) => {
    console.log('renderMarkdownWithAds called with:', { contentLength: content.length, adPlaceholder: adPlaceholder });

    if (!adPlaceholder) {
      console.log('No adPlaceholder, using regular renderMarkdown');
      return renderMarkdown(content);
    }

    // Check for manual ad markers first
    // Supports: <!--AD-->, <!--AD_PLACEHOLDER-->, [AD], [AD_PLACEHOLDER]
    // Also supports HTML comments containing ezoic ad placeholders (for convenience)
    const manualMarkerPatterns = [
      /<!--AD-->/gi,
      /<!--AD_PLACEHOLDER-->/gi,
      /\[AD\]/gi,
      /\[AD_PLACEHOLDER\]/gi,
      // Also match HTML comments containing ezoic ad divs (e.g., <!--<div id="ezoic-pub-ad-placeholder-101"></div>-->)
      // Match any HTML comment that contains ezoic-pub-ad-placeholder anywhere within it (using [\s\S] to match any char including newlines and >)
      /<!--[\s\S]*?ezoic-pub-ad-placeholder[\s\S]*?-->/gi
    ];

    const hasManualMarkers = manualMarkerPatterns.some(pattern => pattern.test(content));

    if (hasManualMarkers) {
      console.log('Manual ad markers detected, using manual placement');
      // Replace manual markers with ad placeholder
      let processedContent = content;
      manualMarkerPatterns.forEach((pattern, index) => {
        const matches = processedContent.match(pattern);
        if (matches) {
          console.log(`Pattern ${index} matched:`, matches[0]);
        }
        processedContent = processedContent.replace(pattern, adPlaceholder);
      });
      console.log('After replacement, adPlaceholder length:', adPlaceholder.length);
      return renderMarkdown(processedContent);
    }

    // No manual markers found - use automatic insertion
    console.log('No manual markers found, using automatic insertion');
    // Split content into paragraphs
    const paragraphs = content.split('\n\n');
    console.log('Split into', paragraphs.length, 'paragraphs');
    const result: string[] = [];

    // Insert ad after every 3rd paragraph (adjust as needed)
    paragraphs.forEach((paragraph, index) => {
      result.push(paragraph);

      // Insert ad after paragraphs 2, 5, 8, etc. (every 3rd paragraph)
      if ((index + 1) % 3 === 0 && index < paragraphs.length - 1) {
        console.log('Inserting ad after paragraph', index + 1);
        result.push(adPlaceholder);
      }
    });

    const finalContent = result.join('\n\n');
    console.log('Final content length:', finalContent.length);
    return renderMarkdown(finalContent);
  };

  if (loading) {
    return (
      <div className="post-page">
        {settings.blogHeaderBanner && settings.blogHeaderBanner.trim() !== '' ? (
          <header className="blog-header-banner" role="banner">
            <div className="banner-image-container">
              <img
                src={settings.blogHeaderBanner}
                alt={`${siteConfig.site_title} Header Banner`}
                className="banner-image"
                loading="eager"
                onError={(e) => {
                  // Fallback if image fails to load
                  console.error('Banner image failed to load');
                  e.currentTarget.style.display = 'none';
                }}
              />
              <div className="banner-tagline-overlay">
                <div className="banner-content">
                  <h1 className="blog-title">
                    <Link to="/" aria-label={`${siteConfig.site_title} Blog - Home`}>
                      {siteConfig.site_title}
                    </Link>
                  </h1>
                  <p className="tagline">{siteConfig.site_tagline}</p>
                </div>
                <nav className="blog-nav" role="navigation" aria-label="Main navigation">
                  <Link to="/cms" className="nav-link" aria-label="Content Management System">
                    Login
                  </Link>
                </nav>
              </div>
            </div>
          </header>
        ) : (
          <header className="blog-header" role="banner">
            <div className="header-content">
              <div className="logo-section">
                <h1 className="blog-title">
                  <Link to="/" aria-label={`${siteConfig.site_title} Blog - Home`}>
                    {siteConfig.site_title}
                  </Link>
                </h1>
                <p className="tagline">{siteConfig.site_tagline}</p>
              </div>
              <nav className="blog-nav" role="navigation" aria-label="Main navigation">
                <Link to="/cms" className="nav-link" aria-label="Content Management System">
                  Login
                </Link>
              </nav>
            </div>
          </header>
        )}

        <main className="post-main">
          <div className="post-content">
            <div className="loading">
              <div className="spinner"></div>
              <p>Loading post...</p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (error) {
    return (
      <div className="post-page">
        {settings.blogHeaderBanner && settings.blogHeaderBanner.trim() !== '' ? (
          <header className="blog-header-banner" role="banner">
            <div className="banner-image-container">
              <img
                src={settings.blogHeaderBanner}
                alt={`${siteConfig.site_title} Header Banner`}
                className="banner-image"
                loading="eager"
                onError={(e) => {
                  // Fallback if image fails to load
                  console.error('Banner image failed to load');
                  e.currentTarget.style.display = 'none';
                }}
              />
              <div className="banner-tagline-overlay">
                <div className="banner-content">
                  <h1 className="blog-title">
                    <Link to="/" aria-label={`${siteConfig.site_title} Blog - Home`}>
                      {siteConfig.site_title}
                    </Link>
                  </h1>
                  <p className="tagline">{siteConfig.site_tagline}</p>
                </div>
                <nav className="blog-nav" role="navigation" aria-label="Main navigation">
                  <Link to="/cms" className="nav-link" aria-label="Content Management System">
                    Login
                  </Link>
                </nav>
              </div>
            </div>
          </header>
        ) : (
          <header className="blog-header" role="banner">
            <div className="header-content">
              <div className="logo-section">
                <h1 className="blog-title">
                  <Link to="/" aria-label={`${siteConfig.site_title} Blog - Home`}>
                    {siteConfig.site_title}
                  </Link>
                </h1>
                <p className="tagline">{siteConfig.site_tagline}</p>
              </div>
              <nav className="blog-nav" role="navigation" aria-label="Main navigation">
                <Link to="/cms" className="nav-link" aria-label="Content Management System">
                  Login
                </Link>
              </nav>
            </div>
          </header>
        )}

        <main className="post-main">
          <div className="post-content">
            <div className="error-message">
              <h2>Error</h2>
              <p>{error}</p>
              <Link to="/" className="back-button">
                ← Back to Home
              </Link>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (!post) {
    return null;
  }

  return (
    <div className="post-page">
      {settings.blogHeaderBanner && settings.blogHeaderBanner.trim() !== '' ? (
        <header className="blog-header-banner" role="banner">
          <div className="banner-image-container">
            <img
              src={settings.blogHeaderBanner}
              alt={`${siteConfig.site_title} Header Banner`}
              className="banner-image"
              loading="eager"
              onError={(e) => {
                // Fallback if image fails to load
                console.error('Banner image failed to load');
                e.currentTarget.style.display = 'none';
              }}
            />
            <div className="banner-tagline-overlay">
              <div className="banner-content">
                <h1 className="blog-title">
                  <Link to="/" aria-label={`${siteConfig.site_title} Blog - Home`}>
                    {siteConfig.site_title}
                  </Link>
                </h1>
                <p className="tagline">{siteConfig.site_tagline}</p>
              </div>
              <nav className="blog-nav" role="navigation" aria-label="Main navigation">
                <Link to="/cms" className="nav-link" aria-label="Content Management System">
                  Login
                </Link>
              </nav>
            </div>
          </div>
        </header>
      ) : (
        <header className="blog-header" role="banner">
          <div className="header-content">
            <div className="logo-section">
              <h1 className="blog-title">
                <Link to="/" aria-label={`${siteConfig.site_title} Blog - Home`}>
                  {siteConfig.site_title}
                </Link>
              </h1>
              <p className="tagline">{siteConfig.site_tagline}</p>
            </div>
            <nav className="blog-nav" role="navigation" aria-label="Main navigation">
              <Link to="/cms" className="nav-link" aria-label="Content Management System">
                Login
              </Link>
            </nav>
          </div>
        </header>
      )}

      <main className="post-main">
        <article className="post-article">
          {/* Featured Image */}
          {(post.featured_image_url || post.featured_image_base64) && (
            <div className="featured-image">
              <img
                src={post.featured_image_base64 || post.featured_image_url}
                alt={post.title}
              />
            </div>
          )}

          {/* Article Header */}
          <header className="article-header">
            <h1 className="article-title">{post.title}</h1>

            {/* Author Metadata */}
            <div className="article-meta">
              <div className="author-info">
                <div className="author-avatar">
                  <div className="avatar-placeholder">
                    {post.author ? post.author.charAt(0).toUpperCase() : (process.env.REACT_APP_SITE_TITLE?.charAt(0).toUpperCase() || 'M')}
                  </div>
                </div>
                <div className="author-details">
                  <span className="author-name">
                    {post.author ? toTitleCase(post.author) : (process.env.REACT_APP_SITE_TITLE || 'SolveSmartHome')}
                  </span>
                </div>
              </div>

              <div className="meta-divider">•</div>

              <div className="meta-item">
                <span className="meta-icon">📅</span>
                <span className="meta-text">{formatDate(post.published_at || post.created_at)}</span>
              </div>

              <div className="meta-divider">•</div>

              <div className="meta-item">
                <span className="meta-icon">📁</span>
                <span className="meta-text">{post.tags ? post.tags.split(',')[0].trim() : 'General'}</span>
              </div>

              <div className="meta-divider">•</div>

              <div className="meta-item">
                <span className="meta-icon">⏱️</span>
                <span className="meta-text">{formatReadingTime(post.body)}</span>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="action-buttons">
              <button
                className="action-button comments"
                onClick={() => setShowComments(!showComments)}
                title={showComments ? "Hide comments" : "Show comments"}
              >
                <span className="button-icon">💬</span>
                <span className="button-count">{comments.length}</span>
                <span className="button-text">Comments</span>
              </button>
              <button
                className="action-button share"
                onClick={handleShare}
                title="Copy link to share this post"
              >
                <span className="button-icon">🔗</span>
                <span className="button-text">
                  {shareMessage || 'Share'}
                </span>
              </button>
            </div>

            {/* Jump to Solution Button */}
            {(post.uc_problem || post.uc_solution || post.uc_product_name) && (
              <div className="utility-jump-section">
                <a href="#utility-card" className="jump-button">
                  <span className="jump-icon">💡</span>
                  Jump to Solution
                </a>
              </div>
            )}
          </header>

          {/* Article Body */}
          <div className="article-body">
            {/* FTC Affiliate Disclosure - above first affiliate link */}
            {(post.uc_product_url) && (
              <div className="affiliate-disclosure" role="note">
                This post contains affiliate links. We may earn a commission at no extra cost to you.
              </div>
            )}
            <div ref={utilityCardRef}>
            <UtilityCard
              problem={post.uc_problem}
              solution={post.uc_solution}
              productName={post.uc_product_name}
              productUrl={post.uc_product_url}
              productImageUrl={post.uc_product_image_url}
              sourceSubreddit={post.uc_source_subreddit}
            />
            </div>
            <div
              className="post-content"
              dangerouslySetInnerHTML={{ __html: renderMarkdownWithAds(post.body, settings.adPlaceholder) }}
            />
          </div>
        </article>

        {/* Sticky Jump to Solution - appears when scrolled past utility card */}
        {hasUtilityCard && showStickyJump && (
          <a href="#utility-card" className="jump-button-sticky" aria-label="Jump to solution">
            <span className="jump-icon">💡</span>
            Jump to Solution
          </a>
        )}

        {/* Comments Section */}
        {showComments && (
          <section className="comments-section">
            <h2 className="comments-title">Comments ({comments.length})</h2>

            {/* Comment Form */}
            <form className="comment-form" onSubmit={handleSubmitComment}>
              <h3 className="comment-form-title">Leave a Comment</h3>
              <div className="comment-form-fields">
                <div className="form-group">
                  <input
                    type="text"
                    placeholder="Your Name"
                    value={newComment.author_name}
                    onChange={(e) => setNewComment({ ...newComment, author_name: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <input
                    type="email"
                    placeholder="Your Email"
                    value={newComment.author_email}
                    onChange={(e) => setNewComment({ ...newComment, author_email: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <textarea
                    placeholder="Write your comment..."
                    value={newComment.content}
                    onChange={(e) => setNewComment({ ...newComment, content: e.target.value })}
                    rows={4}
                    required
                  />
                </div>
                <button
                  type="submit"
                  className="submit-comment-btn"
                  disabled={submittingComment}
                >
                  {submittingComment ? 'Posting...' : 'Post Comment'}
                </button>
              </div>
            </form>

            {/* Comments List */}
            <div className="comments-list">
              {commentsLoading ? (
                <div className="loading-comments">
                  <div className="spinner"></div>
                  <p>Loading comments...</p>
                </div>
              ) : comments.length === 0 ? (
                <div className="no-comments">
                  <p>No comments yet. Be the first to comment!</p>
                </div>
              ) : (
                comments.map((comment) => (
                  <div key={comment.id} className="comment-item">
                    <div className="comment-header">
                      <div className="comment-author">
                        <span className="author-name">{comment.author_name}</span>
                        <span className="comment-date">
                          {formatDate(comment.created_at)}
                        </span>
                      </div>
                      <div className="comment-votes">
                        <button
                          className={`vote-btn upvote ${comment.user_vote === 'upvote' ? 'active' : ''}`}
                          onClick={() => handleVote(comment.id, 'upvote')}
                          title="Upvote"
                        >
                          ▲ {comment.upvotes}
                        </button>
                        <button
                          className={`vote-btn downvote ${comment.user_vote === 'downvote' ? 'active' : ''}`}
                          onClick={() => handleVote(comment.id, 'downvote')}
                          title="Downvote"
                        >
                          ▼ {comment.downvotes}
                        </button>
                      </div>
                    </div>
                    <div className="comment-content">
                      {comment.content}
                    </div>
                  </div>
                ))
              )}
            </div>
          </section>
        )}

        {/* Navigation */}
        <nav className="post-navigation">
          <Link to="/" className="back-button">
            ← Back to All Posts
          </Link>
        </nav>
      </main>

      <footer className="blog-footer" role="contentinfo">
        <div className="footer-content">
          <div className="footer-section">
            <h3 className="footer-title">Categories</h3>
            <nav className="footer-nav" aria-label="Category navigation">
              {siteConfig.site_categories.map((category) => (
                <button
                  key={category}
                  className="footer-link"
                  onClick={() => window.location.href = `/?category=${encodeURIComponent(category)}`}
                >
                  {category}
                </button>
              ))}
            </nav>
          </div>

          <div className="footer-section">
            <h3 className="footer-title">Pages</h3>
            <nav className="footer-nav" aria-label="Page navigation">
              <Link to="/" className="footer-link">Home</Link>
              <Link to="/about" className="footer-link">About</Link>
              <Link to="/privacy" className="footer-link">Privacy</Link>
              <Link to="/cms" className="footer-link">Login</Link>
              <a href="#main-content" className="footer-link">Back to Top</a>
            </nav>
          </div>

          <div className="footer-section">
            <h3 className="footer-title">Connect</h3>
            <p className="footer-text">
              {siteConfig.site_footer_text}
            </p>
          </div>
        </div>

        <div className="footer-bottom">
          <p>&copy; 2024 {siteConfig.site_title} Blog. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default PostPage;
